import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import Section from '../Section/Section';
import ImageCarousel, { type ImageCarouselItem } from '../ImageCarousel/ImageCarousel';
import { GetHomeCarouselList } from '../../Api/apis/carouselApi';
import type { HomeCarouselData } from '../../Api/apis/types/carousel.types';
import { getImageUrl } from '../../Utils/imageUtils';
import styles from './HomeCarousel.style';

export interface HomeCarouselProps {
  /** Width / height ratio of each slide. Defaults to '4 / 1' matching the Figma banner */
  aspectRatio?: string;
  /** Duration between slides in ms. Defaults to 3500ms */
  autoPlaySpeed?: number;
  /** Whether to show left/right arrow controls. Defaults to false */
  showArrows?: boolean;
  /** Whether to show bottom indicator dots. Defaults to true */
  showIndicators?: boolean;
  /** Whether to pause auto-swipe when hovering with mouse. Defaults to false for automatic continuous swiping */
  pauseOnHover?: boolean;
  /** Optional fallback content when no banners exist */
  fallbackContent?: React.ReactNode;
  /** Optional container className */
  className?: string;
}

export const getHomeCarouselImageUrl = (item: HomeCarouselData): string => {
  return getImageUrl(item.image);
};

const mapToImageCarouselItems = (
  items: HomeCarouselData[]
): ImageCarouselItem[] => {
  return items.map((item) => ({
    src: getHomeCarouselImageUrl(item),
    alt: item.title || 'Ecommerce Banner',
    url: item.url,
  }));
};

/**
 * Homepage Carousels component.
 * Fetches active banners from /api/v1/home/carousels/ sorted by ordering.
 */
const HomeCarousel: React.FC<HomeCarouselProps> = ({
  aspectRatio = '4 / 1',
  autoPlaySpeed = 3500,
  showArrows = false,
  showIndicators = true,
  pauseOnHover = false,
  fallbackContent,
  className = '',
}) => {
  const [carouselData, setCarouselData] = useState<HomeCarouselData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchHomeCarousels = async () => {
      try {
        setLoading(true);
        const res = await GetHomeCarouselList();
        if (isMounted) {
          const activeItems = (res.data ?? [])
            .filter((item) => item.is_active)
            .sort((a, b) => a.ordering - b.ordering);
          setCarouselData(activeItems);
        }
      } catch (error) {
        console.error('Error fetching home carousels:', error);
        if (isMounted) {
          setCarouselData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchHomeCarousels();

    return () => {
      isMounted = false;
    };
  }, []);

  // Show a smooth skeleton placeholder while loading to prevent Cumulative Layout Shift (CLS)
  if (loading) {
    return (
      <Section className={`homeCarousel mt-4 mb-2 ${className}`}>
        <Container>
          <div
            className="rounded-4 bg-light overflow-hidden shadow-sm"
            style={styles.skeleton}
          />
        </Container>
      </Section>
    );
  }

  // If no carousels are returned
  if (carouselData.length === 0) {
    if (fallbackContent) {
      return (
        <Section className={`homeCarousel mt-4 mb-2 ${className}`}>
          <Container>{fallbackContent}</Container>
        </Section>
      );
    }
    return null;
  }

  const items = mapToImageCarouselItems(carouselData);

  return (
    <Section className={`homeCarousel mt-4 mb-2 ${className}`}>
      <Container>
        <ImageCarousel
          items={items}
          aspectRatio={aspectRatio}
          autoPlaySpeed={autoPlaySpeed}
          showArrows={showArrows}
          showIndicators={showIndicators}
          pauseOnHover={pauseOnHover}
          indicatorVariant="light"
        />
      </Container>
    </Section>

  );
};

export default HomeCarousel;
